import { useRouter } from 'next/router';
import { briefIsShowable, useMorningBriefQuery } from './brief';
import { BriefState, isBriefPulseCreditPaused, isBriefPulsePaused, resolveBriefState } from './brief-state';
import { usePulses } from './pulses';
import { useWorkspaces, type WorkspaceSummary } from './workspaces';

/**
 * Which morning-brief state the home should draw, and the workspace the `newWorkspace` card names.
 *
 * Lifted out of `BriefSlot` so the TWO homes resolve it the same way. The catch-up header's slot and
 * the Overview home's brief card both ask this question, and a second copy of the resolution is how
 * the two come to disagree about whether the routine is paused — the exact class of bug
 * `resolveBriefState`'s own doc comment is written against. The decision itself still lives in
 * `lib/brief-state.ts`, which is the file mirrored by both phones; this hook only gathers its inputs.
 *
 * ## The preview override
 *
 * `?briefState=<state>` forces the slot so a design can be reviewed without an account that is really
 * credit-paused or user-paused (the native `SYMPHONY_BRIEF_STATE` twin, Golden Rule 8).
 *
 * `newWorkspace` joins the forceable set because its content is the account's OWN first workspace,
 * which any account with a Space already has — but the override is **held until that workspace is in
 * hand**: the card IS the Space, so the catch-up slot draws nothing for it and wraps that nothing in an
 * empty `Card` (review finding). Without a Space, or before the read lands, the resolved state shows.
 * `hero` stays out: it needs a real brief conversation, and a forced one would have nothing to say.
 */
export const PREVIEWABLE_BRIEF_STATES: readonly BriefState[] = ['activate', 'upgrade', 'newWorkspace', 'none'];

export function useBriefSlotState(): { state: BriefState; workspace?: WorkspaceSummary } {
  const { brief, settled: briefSettled } = useMorningBriefQuery();
  const { state: pulses } = usePulses();
  const workspaces = useWorkspaces();
  const router = useRouter();

  const previewState = router.query.briefState;
  const forcedState =
    typeof previewState === 'string' && (PREVIEWABLE_BRIEF_STATES as readonly string[]).includes(previewState)
      ? (previewState as BriefState)
      : undefined;

  const resolvedState = resolveBriefState({
    pulsesLoaded: pulses.kind === 'ready',
    // Credit-paused wins over the user's own pause (resuming won't help without credits). No further
    // gate here: web's upgrade destination is always its own /packages picker — the plan-selector /
    // storefront gating the natives apply to this input is about THEIR destinations (Wixel, App Review
    // 3.1.1), neither of which exists on web.
    pulseCreditPaused: pulses.kind === 'ready' && isBriefPulseCreditPaused(pulses.pulses),
    pulsePaused: pulses.kind === 'ready' && isBriefPulsePaused(pulses.pulses),
    briefSettled,
    // A brief is "there" for the hero if it's showable — a finished brief with content, OR one still in
    // motion (created today, convening). A failed or stale brief is NOT, so the slot falls back to its
    // other states rather than to a prior day's brief.
    hasBrief: briefIsShowable(brief),
    hasWorkspace: workspaces.status === 'ready' && workspaces.workspaces.length > 0,
    // Until the workspaces fetch settles we can't tell newWorkspace from none for a never-seeded
    // routine, so hold `loading` rather than flashing nothing then the card.
    workspacesSettled: workspaces.status === 'ready',
  });

  const workspace = workspaces.status === 'ready' ? workspaces.workspaces[0] : undefined;
  return {
    // A forced `newWorkspace` with no workspace to name is dropped rather than served — see the note
    // above. Every other forceable state draws from copy alone and needs nothing read first.
    state: (forcedState === 'newWorkspace' && !workspace ? undefined : forcedState) ?? resolvedState,
    workspace,
  };
}
